import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Tables } from "../../../components/Elements/Tables/Tables";
import CardsBorder from "../../../components/Cards/CardsBorder";
import { ReactComponent as EyeIcon } from "../../../assets/svgs/eye-icon.svg";
import { ReactComponent as DeleteIcon } from "../../../assets/svgs/deleteIcon.svg";
import CustomButton from "../../../components/Elements/Buttons/CustomButtons";
import PopupCard from "../../../components/Cards/PopupCard/PopupCard";
import AddCustomer from "./AddCustomer";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import CustomSwitch from "../../../components/Elements/CustomSwitch/CustomSwitch";
import {
  fetchAllGroupCustomers,
  setCurrentPage,
  setPageSize,
  deleteGroupClient,
  grantViewAccess,
  revokeViewAccess,
} from "../../../redux/group/groupSlice";

const GroupCustomerDetail = () => {
  const [addCustomer, setAddCustomer] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState({
    visible: false,
    customerId: null,
    customerName: "",
  });
  const [successPopup, setSuccessPopup] = useState({
    visible: false,
    title: "",
    message: "",
  });

  const navigate = useNavigate();
  const location = useLocation();
  const { groupId: groupIdFromParams } = useParams();
  const dispatch = useDispatch();

  const {
    allCustomers,
    totalRecords,
    currentPage,
    pageSize,
    loadingGroupCustomer,
    currentGroup,
  } = useSelector((state) => state.group);
  const groupIdFromState = location.state?.groupId;
  const effectiveGroupId = groupIdFromParams || groupIdFromState || currentGroup?._id;
  const groupNameFromState = currentGroup?.groupName || "Loading...";
  const groupOffsetId = currentGroup?.groupId;

  const handleToggleViewAccess = async (item, forceRevoke = false) => {
    const clientId = item.clientId?._id;
    const currentAccess = item.hasGroupViewAccess;
    
    try {
      if (forceRevoke || currentAccess) {
        await dispatch(revokeViewAccess({ groupId: effectiveGroupId, clientId })).unwrap();
        setSuccessPopup({
          visible: true,
          title: "Access Revoked",
          message: `View access revoked for ${item.clientId?.name}`,
        });
      } else {
        await dispatch(grantViewAccess({ groupId: effectiveGroupId, clientId })).unwrap();
        setSuccessPopup({
          visible: true,
          title: "Access Granted",
          message: `View access granted for ${item.clientId?.name}`,
        });
      }
      // Refresh the list
      // dispatch(fetchAllGroupCustomers({ groupId: effectiveGroupId, pageNo: currentPage, pageSize }));
      setTimeout(() => setSuccessPopup({ visible: false, title: "", message: "" }), 3000);
    } catch (error) {
      console.error("Failed to toggle view access:", error);
    }
  };

  // Table headers
  const headers = useMemo(
    () => [
      { label: "Sr. No.", key: "serial" },
      { label: "Name", key: "customerName" },
      { label: "User ID", key: "customerId" },
      { label: "Valid Till", key: "validTill" },
      { label: "View Access", key: "viewAccess" },
      { label: "Action", key: "action" },
    ],
    [],
  );

  // Pagination handler
  const handlePageChange = useCallback(
    (page, size) => {
      if (size !== pageSize) dispatch(setPageSize(size));
      dispatch(setCurrentPage(page));
      sessionStorage.setItem("groupCustomerPage", page.toString());
    },
    [dispatch, pageSize],
  );

  useEffect(() => {
    const savedPage = sessionStorage.getItem("groupCustomerPage");
    if (savedPage) {
      dispatch(setCurrentPage(parseInt(savedPage, 10)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch all group customers
  useEffect(() => {
    if (!effectiveGroupId) return;
    dispatch(
      fetchAllGroupCustomers({
        groupId: effectiveGroupId,
        pageNo: currentPage,
        pageSize,
      }),
    );
  }, [dispatch, effectiveGroupId, currentPage, pageSize]);

  // Table data
  const tableData = useMemo(
    () =>
      allCustomers.map((item, index) => ({
        key: item._id,
        serial: (currentPage - 1) * pageSize + index + 1,
        customerName: (
          <div className="text-center whitespace-normal break-all max-w-[220px] mx-auto">
            {item.clientId?.organization?.name || item.clientId?.name}
          </div>
        ),
        customerId: <div className="text-center">{item.clientId?.userId}</div>,
        validTill: (
          <div className="text-center">
            {new Date(item.validTill).toLocaleDateString()}
          </div>
        ),
        viewAccess: (
          <div className="flex justify-center">
            <CustomSwitch
              checked={item.hasGroupViewAccess}
              onChange={() => handleToggleViewAccess(item)}
            />
          </div>
        ),
        action: (
          <div className="flex gap-3 justify-center items-center">
            <EyeIcon
              className="cursor-pointer"
              onClick={() =>
                navigate(`/customer/customer-page/${item.clientId?._id}`, {
                  state: {
                    from: location.pathname,
                    userName: item.clientId?.organization?.name || item.clientId?.name,
                  },
                })
              }
            />
            {/* <button
              onClick={() => handleToggleViewAccess(item, true)}
              className="px-3 py-1 text-[10px] font-medium bg-orange-50 text-orange-600 border border-orange-200 rounded hover:bg-orange-600 hover:text-white transition-all whitespace-nowrap"
            >
              Revoke Access
            </button> */}
            <DeleteIcon
              className="cursor-pointer"
              onClick={() =>
                setDeleteConfirmation({
                  visible: true,
                  customerId: item._id,
                  customerName: item.clientId?.name,
                })
              }
            />
          </div>
        ),
      })),
    [allCustomers, currentPage, pageSize, navigate, location.pathname, effectiveGroupId],
  );

  return (
    <div style={{ padding: "20px 40px" }}>
      <CardsBorder>
        <div className="flex justify-between items-center my-2 font-medium px-10">
          <p className="text-xl">Customer</p>
          <CustomButton
            buttonType="primary"
            variant="ok"
            buttonText="+ ADD NEW"
            className="py-6 text-xs font-medium"
            onClick={() => setAddCustomer(true)}
            onSave={() =>
              dispatch(
                fetchAllGroupCustomers({
                  groupId: effectiveGroupId,
                  pageNo: currentPage,
                  pageSize,
                }),
              )
            }
          />
        </div>

        <Tables
          data={tableData}
          headers={headers}
          loading={loadingGroupCustomer}
          pageSize={pageSize}
          total={totalRecords}
          current={currentPage}
          onChange={handlePageChange}
        />
      </CardsBorder>

      {/* Add Customer Modal */}
      {addCustomer && (
        <AddCustomer
          groupId={effectiveGroupId}
          groupName={groupNameFromState}
          groupOffsetId={groupOffsetId}
          onOpen={addCustomer}
          onClose={() => setAddCustomer(false)}
          onSave={() => {
            dispatch(
              fetchAllGroupCustomers({
                groupId: effectiveGroupId,
                pageNo: currentPage,
                pageSize,
              }),
            );
            setAddCustomer(false);
            setSuccessPopup({
              visible: true,
              title: "Success",
              message: "Customer added successfully!",
            });
            setTimeout(
              () => setSuccessPopup({ visible: false, title: "", message: "" }),
              3000,
            );
          }}
        />
      )}

      {/* Delete Confirmation */}
      {deleteConfirmation.visible && (
        <div className="fixed inset-0 bg-black/30 z-[9999] flex items-center justify-center">
          <PopupCard
            type="warning"
            title="Confirm Delete"
            message={`Are you sure you want to delete ${deleteConfirmation.customerName}?`}
            showActions={true}
            confirmText="Yes, Delete"
            cancelText="Cancel"
            onCancel={() =>
              setDeleteConfirmation({
                visible: false,
                customerId: null,
                customerName: "",
              })
            }
            onConfirm={() =>
              dispatch(deleteGroupClient(deleteConfirmation.customerId))
                .unwrap()
                .then(() => {
                  dispatch(
                    fetchAllGroupCustomers({
                      groupId: effectiveGroupId,
                      pageNo: currentPage,
                      pageSize,
                    }),
                  );
                  setDeleteConfirmation({
                    visible: false,
                    customerId: null,
                    customerName: "",
                  });
                  setSuccessPopup({
                    visible: true,
                    title: "Deleted",
                    message: `${deleteConfirmation.customerName} has been deleted successfully!`,
                  });
                  setTimeout(
                    () =>
                      setSuccessPopup({
                        visible: false,
                        title: "",
                        message: "",
                      }),
                    3000,
                  );
                })
            }
          />
        </div>
      )}

      {/* Success Popup */}
      {successPopup.visible && (
        <div className="fixed inset-0 bg-black/30 z-[9999] flex items-center justify-center">
          <PopupCard
            type="success"
            title={successPopup.title}
            message={successPopup.message}
          />
        </div>
      )}
    </div>
  );
};

export default GroupCustomerDetail;
